<script setup>
import { Head } from "@inertiajs/inertia-vue3";
import { StarIcon } from '@heroicons/vue/solid'
import { ShoppingCartIcon } from '@heroicons/vue/outline'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import YouTube from 'vue3-youtube'
import {ref} from "vue";
import ReservationPopup from './ReservationPopup';

defineProps({
    product: Object,
    disabledDates: Array,
    stock: Number,
})

const showReservationPopup = ref(false);

</script>

<template>
    <Head :title="product.data.name" />

    <div class="bg-white">
        <div class="mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:max-w-7xl lg:px-8">
            <!-- Product -->
            <div class="lg:grid lg:grid-rows-1 lg:grid-cols-7 lg:gap-x-8 lg:gap-y-10 xl:gap-x-16">
                <!-- Product image -->
                <div class="lg:row-end-1 lg:col-span-4">
                    <div class="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden">
                        <img :src="product.data.image" alt="product image" class="object-scale-down" />
                    </div>
                </div>

                <!-- Product details -->
                <div class="max-w-2xl mx-auto mt-14 sm:mt-16 lg:max-w-none lg:mt-0 lg:row-end-2 lg:row-span-2 lg:col-span-3">
                    <div class="flex flex-col-reverse">
                        <div class="mt-4">
                            <p class=" mb-1" v-if="product.data.supplier">{{ product.data.supplier.name }}</p>
                            <h1 class="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl">{{ product.data.name }}</h1>
                            <p class="text-lg text-gray-900 sm:text-xl">€{{ product.data.price }} p/d</p>
                            <h2 id="information-heading" class="sr-only">Product informatie</h2>
                        </div>

                        <div>
                            <h3 class="sr-only">Reviews</h3>
                            <div class="flex items-center">
                                <StarIcon v-for="rating in [0, 1, 2, 3, 4]" :key="rating" :class="[parseInt(product.data.rating) > rating ? 'text-yellow-400' : 'text-gray-300', 'h-5 w-5 flex-shrink-0']" aria-hidden="true" />
                            </div>
                            <p class="sr-only">{{ parseInt(product.data.rating) }} van de 5 sterren</p>
                        </div>
                    </div>

                    <p class="text-gray-500 mt-6">{{ product.data.short_description }}</p>

                    <div class="mt-10">
                        <button v-if="stock > 0" @click="showReservationPopup = true" type="button" class="w-full bg-primary border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-primary">
                          <ShoppingCartIcon class="flex-shrink-0 h-6 w-6 text-white group-hover:text-gray-300 mr-3" aria-hidden="true" />
                          Reserveren
                        </button>
                      <button
                        v-if="stock === 0"
                        disabled
                        type="button"
                        class="w-full items-center px-8 py-2 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white">
                        Niet op voorraad
                      </button>
                    </div>
                    <!-- Full description -->
                    <div class="border-t border-gray-200 mt-10 pt-10">
                        <div class="mt-4 prose prose-sm text-gray-500">
                            <div v-html="product.data.full_description"></div>
                        </div>
                    </div>

                    <!-- Categories -->
                    <div class="border-t border-gray-200 mt-10 pt-10">
                        <h3 class="text-sm font-medium text-gray-900">Categorieën</h3>
                        <div class="mt-4 prose prose-sm text-gray-500">
                            <ul role="list">
                                <li v-for="category in product.data.categories" :key="category">{{ category }}</li>
                            </ul>
                        </div>
                    </div>

                    <div class="border-t border-gray-200 mt-10 pt-10">
                        <h3 class="text-sm font-medium text-gray-900">Delen</h3>
                        <ul role="list" class="flex items-center space-x-6 mt-4">
                            <li>
                                <a href="#" class="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-500">
                                    <span class="sr-only">Share on Facebook</span>
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path fill-rule="evenodd" d="M20 10c0-5.523-4.477-10-10-10S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clip-rule="evenodd" />
                                    </svg>
                                </a>
                            </li>
                            <li>
                                <a href="#" class="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-500">
                                    <span class="sr-only">Share on Instagram</span>
                                    <svg class="w-6 h-6" aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
                                        <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" />
                                    </svg>
                                </a>
                            </li>
                            <li>
                                <a href="#" class="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-500">
                                    <span class="sr-only">Share on Twitter</span>
                                    <svg class="w-5 h-5" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                                    </svg>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="w-full max-w-2xl mx-auto mt-16 lg:max-w-none lg:mt-0 lg:col-span-4">
                    <TabGroup as="div">
                        <div class="border-b border-gray-200">
                            <TabList class="-mb-px flex space-x-8">
                                <Tab v-if="product.data.reviews.length > 0" as="template" v-slot="{ selected }">
                                    <button :class="[selected ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-700 hover:text-gray-800 hover:border-gray-300', 'whitespace-nowrap py-6 border-b-2 font-medium text-sm']">Reviews</button>
                                </Tab>
                                <Tab v-if="product.data.kick_protocols.length > 0" as="template" v-slot="{ selected }">
                                    <button :class="[selected ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-700 hover:text-gray-800 hover:border-gray-300', 'whitespace-nowrap py-6 border-b-2 font-medium text-sm']">Protocollen</button>
                                </Tab>
                                <Tab v-if="product.data.attributes.length > 0" as="template" v-slot="{ selected }">
                                    <button :class="[selected ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-700 hover:text-gray-800 hover:border-gray-300', 'whitespace-nowrap py-6 border-b-2 font-medium text-sm']">Kenmerken</button>
                                </Tab>
                                <Tab v-if="product.data.videos.length > 0" as="template" v-slot="{ selected }">
                                    <button :class="[selected ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-700 hover:text-gray-800 hover:border-gray-300', 'whitespace-nowrap py-6 border-b-2 font-medium text-sm']">Videos</button>
                                </Tab>
                            </TabList>
                        </div>
                        <TabPanels as="template">
                            <TabPanel class="-mb-10" v-if="product.data.reviews.length > 0">
                                <h3 class="sr-only">Reviews</h3>

                                <div v-for="(review, reviewIdx) in product.data.reviews" :key="review.id" class="flex text-sm text-gray-500 space-x-4">
                                    <div v-if="false" class="flex-none py-10">
                                        <img :src="review.customer_image || 'data:image/gif;base64,R0lGODlhAQABAAAAACwAAAAAAQABAAA='" alt="" class="w-10 h-10 bg-gray-100 rounded-full" />
                                    </div>
                                    <div :class="[reviewIdx === 0 ? '' : 'border-t border-gray-200', 'py-10']">
                                        <h3 class="font-medium text-gray-900">{{ review.customer_name }}</h3>
                                        <p>
                                            <time :datetime="review.created_at">{{ new Date(review.created_at).toLocaleDateString('nl-NL', {day: "numeric", month: "long", year: "numeric"}) }}</time>
                                        </p>

                                        <div class="flex items-center mt-4">
                                            <StarIcon v-for="rating in [0, 1, 2, 3, 4]" :key="rating" :class="[parseInt(review.rating) > rating ? 'text-yellow-400' : 'text-gray-300', 'h-5 w-5 flex-shrink-0']" aria-hidden="true" />
                                        </div>
                                        <p class="sr-only">{{ review.rating }} van de 5 sterren</p>

                                        <div class="mt-4 prose prose-sm text-gray-500" v-html="review.summary" />
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel as="dl" class="text-sm text-gray-500 pt-10" v-if="product.data.kick_protocols.length > 0">
                                <h3 class="sr-only">Kick protocollen</h3>
                                <div class="px-4 py-5 sm:px-6">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900">Protocollen</h3>
                                </div>
                                <div  v-for="protocol in product.data.kick_protocols" class="mt-5 border-t border-gray-200 px-4 py-5 sm:p-0">
                                    <dl class="divide-y divide-gray-200">
                                        <div class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                            <dt class="text-sm font-medium text-gray-500">{{ protocol.title }}</dt>
                                            <dd class="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                                <span class="ml-4 flex justify-end w-full">
                                                    <a v-if="protocol.pdf" :href="protocol.pdf" class="bg-white rounded-md font-medium text-orange-500 hover:text-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-700">Download</a>
                                                </span>
                                            </dd>
                                        </div>
                                    </dl>
                                </div>
                            </TabPanel>

                            <TabPanel class="pt-10" v-if="product.data.attributes">
                                <h3 class="sr-only">Kenmerken</h3>
                                <div class="bg-white overflow-hidden sm:rounded-lg">
                                    <div class="px-4 py-5 sm:px-6">
                                        <h3 class="text-lg leading-6 font-medium text-gray-900">Kenmerken</h3>
                                    </div>
                                    <div class="border-t border-gray-200 px-4 py-5 sm:p-0">
                                        <dl class="sm:divide-y sm:divide-gray-200">
                                            <div v-for="attribute in product.data.attributes" class="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                                <dt class="text-sm font-medium text-gray-500">{{ attribute.title }}</dt>
                                                <dd class="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{{  attribute.description }}</dd>
                                            </div>
                                        </dl>
                                    </div>
                                </div>
                            </TabPanel>

                            <TabPanel class="pt-10" v-if="product.data.videos.length > 0">
                                <h3 class="sr-only">Videos</h3>
                                <div class="bg-white overflow-hidden">
                                    <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
                                        <h3 class="text-lg leading-6 font-medium text-gray-900">Videos</h3>
                                    </div>
                                    <div v-for="video in product.data.videos" class="px-4 py-5 sm:p-0">
                                        <h3 class="text-md font-medium my-5"> {{ video.title }}</h3>
                                        <YouTube
                                            :src="video.video"
                                            width="100%"
                                            ref="youtube" />
                                    </div>
                                </div>
                            </TabPanel>
                        </TabPanels>
                    </TabGroup>
                </div>
            </div>
        </div>
    </div>
    <ReservationPopup v-model="showReservationPopup" :product="product.data" :disabled-dates="disabledDates"/>
</template>
